/**
 * EPIC FLOW strings. Arabic is the source language (CONVENTIONS §8).
 *
 * Copy is transcribed from `design/MedOSOps.dc.html` (`dashReception`,
 * `dashDoctor`, `arrival`, `waiting`) and `design/MedOSAuthPublic.dc.html`
 * (`tv`) rather than invented, so the screens read as the design does.
 */
export const flowAr: Record<string, string> = {
  // --- Reception dashboard -------------------------------------------------
  'flow.reception.greeting.morning': 'صباح الخير، {name}',
  'flow.reception.greeting.evening': 'مساء الخير، {name}',
  'flow.reception.subtitle': '{date} · {branch} · {doctors} أطباء في الدوام',
  'flow.reception.registerPatient': 'تسجيل مريض',
  'flow.reception.bookAppointment': '+ حجز موعد',
  'flow.reception.upcoming': 'الوصول القادم',
  'flow.reception.allAppointments': 'كل المواعيد ←',
  'flow.reception.clinicStatus': 'حالة العيادات',
  'flow.reception.search': 'بحث عن مريض',
  'flow.reception.searchPlaceholder': 'الاسم، الجوال، رقم الهوية أو رقم الملف',
  'flow.reception.searchHint': 'أدخل حرفين على الأقل',
  'flow.reception.searchEmpty': 'لا توجد نتائج مطابقة',
  'flow.reception.noUpcoming': 'لا مواعيد قادمة اليوم',
  'flow.reception.noDoctors': 'لا يوجد أطباء مرتبطون بهذا الفرع',

  // --- KPIs ----------------------------------------------------------------
  'flow.kpi.today': 'مواعيد اليوم',
  'flow.kpi.waiting': 'في الانتظار',
  'flow.kpi.averageWait': 'متوسط الانتظار',
  'flow.kpi.readyCheckout': 'جاهز للمحاسبة',
  'flow.kpi.completed': 'مكتملة',
  'flow.kpi.inProgress': 'قيد الكشف',
  'flow.kpi.drafts': 'مسودات غير مثبّتة',
  'flow.kpi.noShow': 'لم يحضر',
  'flow.kpi.minutesSuffix': ' د',

  // --- Doctor dashboard and queue -----------------------------------------
  'flow.doctor.title': 'طابور اليوم — {doctor}',
  'flow.doctor.subtitle': '{specialty} · {appointments} موعداً · {drafts} مسودات زيارة مفتوحة',
  'flow.doctor.startNext': 'بدء الكشف التالي',
  /** Shown on an ACTIVE_ENCOUNTER_LIMIT refusal, linking to the open one. */
  'flow.doctor.openBlocking': 'افتح الاستشارة غير المنتهية',
  'flow.doctor.noDoctorRecord': 'حسابك غير مرتبط بسجل طبيب، لذلك لا يوجد طابور لعرضه.',
  'flow.doctor.emptyQueue': 'لا يوجد مرضى في طابورك الآن',
  'flow.doctor.tab.waiting': 'في الانتظار',
  'flow.doctor.tab.inProgress': 'قيد الكشف',
  'flow.doctor.tab.readyCheckout': 'جاهز للمحاسبة',
  'flow.doctor.tab.completed': 'مكتملة',

  // --- Waiting room --------------------------------------------------------
  'flow.waiting.title': 'غرفة الانتظار',
  'flow.waiting.subtitle': '{branch} · تحديث بالاستطلاع كل {seconds} ثانية',
  'flow.waiting.tvMode': 'وضع شاشة العرض',
  'flow.waiting.recordArrival': 'تسجيل وصول',
  'flow.waiting.empty': 'لا يوجد أحد في غرفة الانتظار',
  'flow.column.arrived': 'وصل',
  'flow.column.inProgress': 'قيد الكشف',
  'flow.column.readyCheckout': 'جاهز للمحاسبة',
  'flow.column.completed': 'مكتمل',
  'flow.column.empty': 'لا أحد',

  // --- Actions -------------------------------------------------------------
  'flow.action.completeArrival': 'استكمال تسجيل الوصول',
  'flow.action.arrive': 'تسجيل وصول',
  'flow.action.start': 'بدء الكشف',
  'flow.action.readyCheckout': 'جاهز للمحاسبة',
  'flow.action.checkout': 'محاسبة',
  'flow.action.startVisit': 'فتح الزيارة',
  'flow.action.openVisit': 'متابعة الزيارة',
  'flow.action.retry': 'إعادة المحاولة',
  'flow.action.cancel': 'إلغاء',
  'flow.action.back': 'رجوع',
  'flow.action.next': 'التالي',

  // --- Disabled reasons ----------------------------------------------------
  'flow.disabled.noPermission': 'لا تملك صلاحية تنفيذ هذا الإجراء.',
  'flow.disabled.wrongState': 'حالة الموعد الحالية لا تسمح بهذا الإجراء.',
  'flow.disabled.doctorBusy': 'لدى الطبيب كشف جارٍ بالفعل.',
  'flow.disabled.notYours': 'هذا الموعد يتبع طبيباً آخر.',

  // --- Wait time -----------------------------------------------------------
  'flow.wait.minutesShort': 'د',
  'flow.wait.notArrived': 'لم يصل',
  'flow.wait.threshold': 'حد الانتظار {minutes} دقيقة',
  'flow.freshness.updating': 'جارٍ التحديث…',
  'flow.freshness.updated': 'آخر تحديث قبل {seconds} ثانية',
  'flow.subject.provisional': 'غير مُعرَّف',

  // --- Arrival -------------------------------------------------------------
  'flow.arrival.title': 'تسجيل الوصول',
  'flow.arrival.subtitle': 'خطوتان: تأكيد الهوية، ثم سياسة الرسوم وإصدار رقم الدور.',
  'flow.arrival.step1': 'الهوية والتنبيهات',
  'flow.arrival.step2': 'الرسوم والتأكيد',
  'flow.arrival.identityConfirmed': '✓ هوية مؤكدة',
  'flow.arrival.provisionalContact': 'حجز عام — لم يُعرَّف المريض بعد',
  'flow.arrival.identifyExisting': 'ربط بمريض مسجّل',
  'flow.arrival.createNew': 'إنشاء ملف مريض جديد',
  'flow.arrival.fullName': 'الاسم الكامل',
  'flow.arrival.phone': 'رقم الجوال',
  'flow.arrival.nationalId': 'رقم الهوية',
  'flow.arrival.duplicates': 'مرضى مطابقون محتملون',
  'flow.arrival.duplicatesBody':
    'راجع هذه الملفات قبل إنشاء ملف جديد. المتابعة رغم التطابق تتطلب سبباً مكتوباً.',
  'flow.arrival.overrideReason': 'سبب تجاوز التطابق',
  'flow.arrival.overrideReasonHint': 'يُسجَّل في سجل التدقيق مع اسمك ووقت التنفيذ.',
  'flow.arrival.alerts': 'تنبيهات سريرية',
  'flow.arrival.alertsHidden': 'التنبيهات السريرية غير متاحة لصلاحيتك.',
  'flow.arrival.noAlerts': 'لا توجد تنبيهات سريرية نشطة.',
  'flow.arrival.balance': 'رصيد مستحق',
  'flow.arrival.balanceHidden': 'الرصيد المالي غير متاح لصلاحيتك.',
  'flow.arrival.balanceInvoices': '{count} فاتورة مستحقة',
  'flow.arrival.noBalance': 'لا يوجد رصيد مستحق.',
  'flow.arrival.feePolicy': 'سياسة الرسوم',
  'flow.arrival.feeReason': 'سبب الإعفاء',
  'flow.arrival.feeReasonHint': 'مطلوب للإعفاء الكامل ويُسجَّل في سجل التدقيق.',
  'flow.arrival.room': 'العيادة',
  'flow.arrival.confirm': 'تأكيد الوصول وإصدار رقم الدور',
  'flow.arrival.suggestedQueue': 'رقم الدور المقترح',
  'flow.arrival.queueNote': 'الرقم النهائي يصدر عند التأكيد وقد يختلف إذا سجّل زميل وصولاً قبلك.',
  'flow.arrival.details': 'تفاصيل الموعد',
  'flow.arrival.detail.time': 'الوقت',
  'flow.arrival.detail.service': 'الخدمة',
  'flow.arrival.detail.source': 'المصدر',
  'flow.arrival.detail.doctor': 'الطبيب',
  'flow.arrival.detail.branch': 'الفرع',
  'flow.arrival.atomicNote':
    'جميع خطوات الوصول (ربط المريض، قرار الرسوم، رقم الدور، تغيير الحالة) تُنفَّذ في معاملة واحدة — إما تنجح كاملة أو تفشل كاملة.',
  'flow.arrival.done': 'تم تسجيل الوصول. رقم الدور {number}.',
  'flow.arrival.needsRepair':
    'سُجّل وصول هذا المريض دون رقم دور ودون تحديد الرسوم. أكمل التسجيل الآن.',
  'flow.arrival.alreadyArrived': 'سُجِّل وصول هذا الموعد بالفعل.',
  'flow.arrival.needSubject': 'اختر مريضاً مسجّلاً أو أنشئ ملفاً جديداً قبل المتابعة.',

  // --- Fee policies --------------------------------------------------------
  'flow.fee.STANDARD': 'كشف أول — رسوم كاملة',
  'flow.fee.FOLLOW_UP': 'متابعة — رسوم المتابعة',
  'flow.fee.SERVICE_PRICE': 'حسب سعر الخدمة المعتمد',
  'flow.fee.EXEMPT': 'إعفاء كامل من الرسوم',
  'flow.fee.FREE': 'مجاني',
  'flow.fee.unavailable': 'غير متاح لهذا الموعد أو لصلاحيتك',
  'flow.fee.requiresWaiver': 'يتطلب صلاحية «اعتماد الإعفاءات» وسبباً مكتوباً',

  // --- TV mode -------------------------------------------------------------
  'flow.tv.current': 'الدور الحالي',
  'flow.tv.next': 'الأدوار التالية',
  'flow.tv.waitingHall': '{branch} — قاعة الانتظار',
  'flow.tv.privacyNote': 'لا تُعرض أسماء المرضى أو أي بيانات سريرية على هذه الشاشة.',
  'flow.tv.exit': 'خروج من وضع العرض',
  'flow.tv.noCurrent': '—',
  'flow.tv.noNext': 'لا توجد أدوار في الانتظار',

  // --- Conflicts -----------------------------------------------------------
  'flow.conflict.stale': 'تغيّر هذا الموعد بعد أن فتحت الشاشة. أعد التحميل قبل المحاولة.',
  'flow.conflict.duplicates': 'يوجد مرضى مطابقون. راجعهم أو اكتب سبب التجاوز.',
};
